import type { Period, Task } from 'fhir/r4';
import { v4 as uuid } from 'uuid';

import * as FHIRUtils from '../fhirutils';

export function build(
  taskCode: { code: string | undefined; display: string | undefined },
  // `null` at every call site that has no identifiers to attach; the body guards
  // with `if (identifiers)` before assigning, so absence is the designed input.
  identifiers: [{ system: string; value: string }] | null,
  description: string | undefined,
  forRef: string, //eg: Patient/patient1
  basedOnRefs: string[], //eg: ServiceRequest/exampel1
  // Same as `identifiers`: `null` where there is no focus resource, guarded by
  // `if (focusRef)` below.
  focusRef: string | null | undefined, //eg: Questionnaire/exampleQ
  // `null` for a task created with no reason selected; lands in `reasonCode.text`,
  // which FHIR leaves `0..1`.
  reason: string | null,
  // `null` when no note was entered; guarded by `if (note)` before use.
  note: string | null | undefined,
  // `null` at the call sites that create a standalone task; guarded by
  // `if (partofRef)` before use, same as `identifiers` and `focusRef`.
  partofRef?: string | null, //parent taksId,
  startDate?: string,
  endDate?: string,
  ownerRef?: { reference: string; display: string },
  updatedBy?: string,
  // `null` where no vendor was chosen, guarded by `if (recipientRef)` below.
  // `display` is `0..1` on a FHIR `Reference`, and the Add Task form derives it from
  // a `find` over the vendor list, so it is absent for an unlisted vendor.
  recipientRef?: Array<{ reference: string; display: string | undefined }> | null,
  referralId?: string,
  // Accepted but unread by this builder; every call site passes `null`.
  org?: string | null,
  priority?: 'routine' | 'urgent' | 'asap' | 'stat'
  // `id` is always assigned from `uuid()` below, so it is present on every task this
  // builder returns — unlike `Task.id`, which FHIR leaves `0..1`. Callers that hand
  // the new id straight to a consumer expecting a `string` rely on that.
): Task & { id: string } {
  const taskId = uuid();

  let task: Task & { id: string } = {
    resourceType: 'Task',
    id: taskId,
    description: description,
    status: 'requested',
    intent: 'order',
    reasonCode: {
      /*
       * `CodeableConcept.text` is declared `string | undefined`, but a `null`
       * `reason` is what several call sites pass and what this has always written.
       * `JSON.stringify` keeps `null` and drops `undefined`, so the request body
       * differs — the cast preserves the body exactly rather than changing what
       * reaches the FHIR proxy.
       */
      text: reason as string | undefined,
    },
  };
  task.priority = priority ? priority : 'routine';
  if (taskCode) {
    task.code = {
      coding: [
        {
          system: 'https://projectwell.io/fhir/codes/task-codes',
          code: taskCode.code,
          display: taskCode.display,
        },
      ],
    };
  }

  if (identifiers) {
    task.identifier = identifiers;
  }

  if (referralId) {
    task.groupIdentifier = {
      system: 'https://projectwell.io/fhir/identifiers/referral-id',
      value: referralId,
    };
  }

  if (basedOnRefs && basedOnRefs.length > 0) {
    task.basedOn = [];
    basedOnRefs.forEach((basedOnRef) => {
      task.basedOn?.push({
        reference: basedOnRef,
      });
    });
  }

  if (focusRef) {
    task.focus = {
      reference: focusRef,
    };
  }

  if (forRef) {
    task.for = {
      reference: forRef,
    };
  }

  if (note) {
    task.note = [
      {
        text: note,
      },
    ];
  }

  if (partofRef) {
    task.partOf = [
      {
        reference: partofRef,
      },
    ];
  }

  if (ownerRef) {
    task.owner = ownerRef;
  }

  if (startDate) {
    const executionPeriod: Period = {
      start: startDate,
    };
    if (endDate) {
      executionPeriod.end = endDate;
    } else {
      executionPeriod.end = startDate;
    }
    task.executionPeriod = executionPeriod;
  }

  if (updatedBy) {
    task = FHIRUtils.addUpdatedBy(task, updatedBy);
  }

  if (recipientRef) {
    task.restriction = {
      recipient: recipientRef,
    };
    if (!task.identifier) {
      task.identifier = [
        {
          system: 'https://projectwell.io/fhir/identifiers/vendor-codes',
          value: recipientRef[0]!.reference?.split('/')?.[1],
        },
      ];
    } else {
      task.identifier = task.identifier.concat([
        {
          system: 'https://projectwell.io/fhir/identifiers/vendor-codes',
          value: recipientRef[0]!.reference?.split('/')?.[1],
        },
      ]);
    }
  }
  return task;
}

export function perform() {
  //complete task. (status update)
  //get next task from PlanDef/ActivityDef
  //create new Task for next step. so that the Task reader/Queue reader or UI will display tasks
}
